// src/hooks/usePhoneAuth.js
import { useState, useCallback, useEffect, useRef } from 'react';
import {
  getAuth,
  initializeRecaptchaConfig,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  linkWithCredential,
  signInWithCredential,
  signOut, // fallback if signOutSafely not provided
} from 'firebase/auth';
import { getFriendlyFirebaseError } from '../utils/firebaseErrorMessages';

export default function usePhoneAuth({
  onSuccess,
  onError,
  testMode = false,
  // Optional: stronger sign-out injected by caller (e.g., from useProfileEditor)
  signOutSafely,
} = {}) {
  const auth = getAuth();
  const recaptchaVerifierRef = useRef(null);
  const verificationIdRef = useRef(null);

  // Keep a verified phone credential if we want to reuse it to sign in.
  const lastPhoneCredRef = useRef(null);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'verifying' | 'sent' | 'done' | 'error'
  const [error, setError] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Mode + conflict flag
  const [mode, setMode] = useState('link');             // 'link' | 'signin'
  const [isPhoneAlreadyInUse, setIsPhoneAlreadyInUse] = useState(false);

  const ensureRecaptchaVerifier = useCallback(async () => {
    if (testMode || recaptchaVerifierRef.current) return recaptchaVerifierRef.current;

    try {
      await initializeRecaptchaConfig(auth);
    } catch (err) {
      console.warn("reCAPTCHA Enterprise config failed:", err);
    }

    if (!recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current = new RecaptchaVerifier(
        auth,
        'recaptcha-container',
        { size: 'invisible' }
      );
    }

    return recaptchaVerifierRef.current;
  }, [auth, testMode]);

  // Build the invisible reCAPTCHA once
  useEffect(() => {
    if (testMode) return;
    if (recaptchaVerifierRef.current) return;

    let cancelled = false;
    const initVerifier = async () => {
      await ensureRecaptchaVerifier();
      if (cancelled && recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear?.();
        recaptchaVerifierRef.current = null;
      }
    };

    initVerifier();
    return () => {
      cancelled = true;
    };
  }, [ensureRecaptchaVerifier, testMode]);

  // Resend countdown
  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      setCanResend(false);
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    } else {
      setCanResend(status === 'sent');
    }
    return () => clearTimeout(timer);
  }, [resendTimer, status]);

  const sendCode = async () => {
    setError('');
    setIsPhoneAlreadyInUse(false);
    setOtp(''); // ensure a clean OTP field for the incoming code

    if (!phoneNumber) {
      setStatus('error');
      setError('Please enter a valid phone number.');
      onError?.();
      return;
    }

    try {
      setStatus('verifying');
      const recaptchaVerifier = await ensureRecaptchaVerifier();
      const result = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        recaptchaVerifier
      );
      verificationIdRef.current = result.verificationId;
      setStatus('sent');
      setResendTimer(30);
    } catch (err) {
      const message = getFriendlyFirebaseError(err);
      setError(message);
      setStatus('error');
      onError?.(err);
    }
  };

  // Detect "already in use" and expose the recovery path
  const markConflict = (err) => {
    const code = err?.code || '';
    const msg = (getFriendlyFirebaseError(err) || '').toLowerCase();
    const looksLikeConflict =
      code === 'auth/credential-already-in-use' ||
      code === 'auth/account-exists-with-different-credential' ||
      code === 'auth/phone-number-already-exists' ||
      msg.includes('already been verified') ||
      msg.includes('already in use');

    if (looksLikeConflict) {
      setIsPhoneAlreadyInUse(true);
      setError('This phone number has already been verified by another account.');
      setOtp(''); // clear OTP so the next flow starts clean
      setStatus('idle'); // return to idle to show the CTA under the phone field
      try {
        if (verificationIdRef.current && otp) {
          lastPhoneCredRef.current = PhoneAuthProvider.credential(
            verificationIdRef.current,
            otp
          );
        }
      } catch {
        lastPhoneCredRef.current = null;
      }
      return true;
    }
    return false;
  };

  const autoResendOnExpired = async () => {
    // Used when we detect code/session expired
    try {
      setOtp(''); // clear stale input before sending new code
      const recaptchaVerifier = await ensureRecaptchaVerifier();
      const result = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        recaptchaVerifier
      );
      verificationIdRef.current = result.verificationId;
      lastPhoneCredRef.current = null; // don't reuse old code anymore
      setStatus('sent');
      setResendTimer(30);
      setError('Your previous code expired. We sent a new one.');
    } catch (err) {
      const message = getFriendlyFirebaseError(err);
      setStatus('error');
      setError(message);
      onError?.(err);
    }
  };

  const isExpiredCodeError = (err) => {
    const code = err?.code || '';
    const msg = (err?.message || '').toLowerCase();
    return (
      code === 'auth/code-expired' ||
      code === 'auth/invalid-verification-code' || // sometimes reported for stale codes
      code === 'auth/session-expired' ||
      msg.includes('expired')
    );
  };

  const verifyCode = async () => {
    setError('');
    if (!otp || !verificationIdRef.current) {
      setError('Please enter the code.');
      setStatus('error');
      onError?.();
      return;
    }

    setStatus('verifying');
    try {
      const cred = PhoneAuthProvider.credential(
        verificationIdRef.current,
        otp
      );

      if (mode === 'signin') {
        await signInWithCredential(auth, cred);
        lastPhoneCredRef.current = null;
        setStatus('done');
        onSuccess?.();
        return;
      }

      // Default: attempt to link to current user
      const user = auth.currentUser;
      if (!user) throw new Error('No authenticated user.');
      await linkWithCredential(user, cred);

      lastPhoneCredRef.current = null;
      setStatus('done');
      onSuccess?.();
    } catch (err) {
      if (isExpiredCodeError(err)) {
        await autoResendOnExpired();
        return;
      }
      if (markConflict(err)) {
        onError?.(err);
        return;
      }
      const message = getFriendlyFirebaseError(err);
      setError(message);
      setStatus('error');
      onError?.(err);
    }
  };

  const resendCode = async () => {
    if (!canResend) return;
    await sendCode();
  };

  const changeNumber = () => {
    setOtp('');
    verificationIdRef.current = null;
    lastPhoneCredRef.current = null;
    setStatus('idle');
    setError('');
    setIsPhoneAlreadyInUse(false);
    setMode('link');
  };

  // Prefer the injected safe sign-out; otherwise fall back to Firebase signOut.
  const doSafeSignOut = async () => {
    if (typeof signOutSafely === 'function') {
      await signOutSafely({ redirect: false });
    } else {
      await signOut(auth);
    }
  };

  // Secondary CTA:
  // - Sign out current session
  // - Switch to 'signin' mode
  // - Reuse credential if we have it; else send a new OTP
  const startSignInInstead = async () => {
    try {
      setError('');
      setStatus('verifying');

      await doSafeSignOut();
      setMode('signin');
      setOtp(''); // ensure field is clear so the new OTP can arrive or be typed

      if (lastPhoneCredRef.current) {
        try {
          await signInWithCredential(auth, lastPhoneCredRef.current);
          lastPhoneCredRef.current = null;
          setStatus('done');
          onSuccess?.();
          return;
        } catch (err) {
          if (isExpiredCodeError(err)) {
            await autoResendOnExpired();
            return;
          }
          const message = getFriendlyFirebaseError(err);
          setError(message);
          setStatus('error');
          onError?.(err);
          return;
        }
      }

      // No saved credential – start fresh OTP sign-in
      const recaptchaVerifier = await ensureRecaptchaVerifier();
      const result = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        recaptchaVerifier
      );
      verificationIdRef.current = result.verificationId;
      setStatus('sent');
      setResendTimer(30);
    } catch (err) {
      const message = getFriendlyFirebaseError(err);
      setError(message);
      setStatus('error');
      onError?.(err);
    }
  };

  return {
    phoneNumber,
    setPhoneNumber,
    otp,
    setOtp,
    status,
    error,
    canResend,
    resendTimer,
    sendCode,
    verifyCode,
    resendCode,
    changeNumber,

    mode,
    isPhoneAlreadyInUse,
    startSignInInstead,
  };
}
